import { Shield, ArrowLeft, ArrowRight, Lock, Eye, Server, Bell, UserCheck, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { COMPANY_INFO } from '../config/company';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { LEGAL_CONTENT } from '../config/legalContent';

// خريطة الأيقونات — تسمح للملف المركزي بإسناد أيقونة بواسطة اسمها فقط
const ICONS = {
  Lock: <Lock className="h-5 w-5" />,
  Eye: <Eye className="h-5 w-5" />,
  Server: <Server className="h-5 w-5" />,
  Bell: <Bell className="h-5 w-5" />,
  UserCheck: <UserCheck className="h-5 w-5" />,
  FileText: <FileText className="h-5 w-5" />,
};

export default function PrivacyPolicy() {
  const { t, language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const lang = isAr ? 'ar' : 'en';
  const BackArrow = isAr ? ArrowRight : ArrowLeft;

  const P = LEGAL_CONTENT.privacy;

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#F8FAFC]'}`} dir={isAr ? 'rtl' : 'ltr'}>
      {/* Background glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className={`absolute top-20 start-1/4 w-[400px] h-[400px] ${isDarkMode ? 'bg-[#1E9BD9]/10' : 'bg-[#1E9BD9]/5'} rounded-full blur-[100px]`} />
      </div>

      {/* Compact Header */}
      <section className="relative pt-10 pb-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-[#1E9BD9] hover:underline mb-6 text-sm" data-testid="privacy-back-home">
            <BackArrow className="h-4 w-4" />
            {t('pages.backToHome')}
          </Link>
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isDarkMode ? 'bg-[#1E9BD9]/10 border border-[#1E9BD9]/20' : 'bg-[#1E9BD9]/10'}`}>
              <Shield className="h-7 w-7 text-[#1E9BD9]" />
            </div>
            <div>
              <h1 className={`text-3xl sm:text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`} data-testid="privacy-title">
                {t('pages.privacyTitle')}
              </h1>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>
                {t('pages.privacySubtitle')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="relative pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`rounded-2xl p-6 md:p-10 ${isDarkMode ? 'bg-[#141414] border border-white/5' : 'bg-white border border-[#E2E8F0] shadow-sm'}`}>
            <p className={`mb-8 leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-[#475569]'}`}>
              {P.intro[lang]}
            </p>

            <div className="space-y-8">
              {P.sections.map((section, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-[#1E9BD9]/10' : 'bg-[#1E9BD9]/10'}`}>
                      <div className="text-[#1E9BD9]">{ICONS[section.iconKey]}</div>
                    </div>
                  </div>
                  <div>
                    <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{section[lang].title}</h3>
                    <p className={`leading-relaxed text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#475569]'}`}>{section[lang].content}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Data deletion link */}
            <div className={`mt-10 p-4 rounded-xl flex flex-wrap items-center justify-between gap-3 ${isDarkMode ? 'bg-[#1E9BD9]/5 border border-[#1E9BD9]/15' : 'bg-[#1E9BD9]/5 border border-[#1E9BD9]/10'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-[#475569]'}`}>
                {P.dataDeletionCta[lang].text}
              </p>
              <Link to="/data-deletion" className="text-sm font-medium text-[#1E9BD9] hover:underline" data-testid="privacy-data-deletion-link">
                {P.dataDeletionCta[lang].link}
              </Link>
            </div>

            <div className={`mt-10 pt-8 border-t ${isDarkMode ? 'border-white/10' : 'border-[#E2E8F0]'}`}>
              <h3 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                {P.contact[lang].heading}
              </h3>
              <p className={isDarkMode ? 'text-gray-400' : 'text-[#475569]'}>
                {P.contact[lang].line}
                <a href={`mailto:${COMPANY_INFO.email}`} className="text-[#1E9BD9] hover:underline">
                  {COMPANY_INFO.email}
                </a>
              </p>
              <p className={`text-sm mt-4 ${isDarkMode ? 'text-gray-600' : 'text-[#94A3B8]'}`}>
                {LEGAL_CONTENT.lastUpdated[lang]}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
